/**
 * carousel-animate.js
 * Shared utility: auto-play + IntersectionObserver pause/resume for
 * every hero carousel and horizontal-scroll section on the site.
 *
 * Features added (2026-05-09):
 *  - Heartbeat pulse button injected into every carousel parent
 *  - Tap/click on the hero banner pauses animation
 *  - Exposed isPaused state and manual pause/resume
 *
 * Usage:
 *   import { initCarousel, initHScroll } from '/js/utils/carousel-animate.js';
 *
 *   initCarousel({
 *     track:    document.getElementById('ac-track'),
 *     dots:     document.querySelectorAll('#ac-dots .ac-dot'),
 *     interval: 5000,
 *     onSlide:  (idx) => {}
 *   });
 */

export function initCarousel({ track, dots = [], bars = [], interval = 5000, onSlide, fixedHeight = false } = {}) {
  if (!track) return;

  let cur = 0;
  let timer = null;
  let paused = false;
  const total = dots.length || Math.round(track.scrollWidth / track.offsetWidth);
  const parent = track.parentElement || track;

  // Shrink the carousel container to the active slide's content height at all
  // breakpoints, eliminating empty space caused by flex-stretch on shorter slides.
  // Callers that want a fixed full-viewport hero instead (fixedHeight: true) skip
  // this entirely and let the container's own CSS height (e.g. 100vh/100dvh) stand
  // — content-based shrinking is what made those heroes inconsistent in height
  // from slide to slide and shorter than the viewport.
  function syncHeight() {
    if (fixedHeight) return;
    const slide = Array.from(track.children)[cur];
    if (!slide) return;
    const inner = slide.firstElementChild;
    if (!inner) return;
    const cs = window.getComputedStyle(slide);
    const vPad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const h = inner.offsetHeight + vPad;
    const minH = window.innerWidth <= 768 ? 180 : 300;
    parent.style.minHeight = '0';
    parent.style.height = Math.max(h, minH) + 'px';
  }

  function sync(idx) {
    dots.forEach((d, i) => d.classList.toggle('active', i === idx));
    bars.forEach((b, i) => b.classList.toggle('active', i === idx));
    syncHeight();
    if (typeof onSlide === 'function') onSlide(idx);
  }

  function goTo(idx) {
    const next = ((idx % total) + total) % total;
    const isWrap = (cur === total - 1 && next === 0) || (cur === 0 && next === total - 1);
    cur = next;
    track.scrollTo({ left: cur * track.offsetWidth, behavior: isWrap ? 'instant' : 'smooth' });
    sync(cur);
    if (!paused) resetTimer();
  }

  function step(dir) { goTo(cur + dir); }
  function advance() { goTo(cur + 1); }

  function startTimer() {
    if (timer || paused) return;
    timer = setInterval(advance, interval);
  }

  function stopTimer() {
    clearInterval(timer);
    timer = null;
  }

  function resetTimer() {
    stopTimer();
    startTimer();
  }

  function pause() {
    paused = true;
    stopTimer();
    updateHeartbeatBtn();
  }

  function resume() {
    paused = false;
    startTimer();
    updateHeartbeatBtn();
  }

  function togglePause() {
    paused ? resume() : pause();
  }

  // ── Heartbeat pause button ──
  let heartbeatBtn = null;

  function updateHeartbeatBtn() {
    if (!heartbeatBtn) return;
    heartbeatBtn.setAttribute('aria-label', paused ? 'Resume slideshow' : 'Pause slideshow');
    heartbeatBtn.classList.toggle('hb-paused', paused);
    heartbeatBtn.title = paused ? 'Click to resume' : 'Click to pause';
  }

  function injectHeartbeatBtn() {
    if (parent.querySelector('.hb-pause-btn')) return;
    heartbeatBtn = document.createElement('button');
    heartbeatBtn.className = 'hb-pause-btn';
    heartbeatBtn.setAttribute('aria-label', 'Pause slideshow');
    heartbeatBtn.title = 'Click to pause';
    heartbeatBtn.innerHTML = `
      <span class="hb-ring"></span>
      <span class="hb-icon hb-icon--pause">
        <span></span><span></span>
      </span>
      <span class="hb-icon hb-icon--play">&#9654;</span>
    `;
    heartbeatBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      togglePause();
    });
    parent.appendChild(heartbeatBtn);
  }

  // ── Tap-to-pause on the hero banner ──
  function onBannerClick(e) {
    if (e.target.closest('.hb-pause-btn')) return;
    if (e.target.closest('a') || e.target.closest('button')) return;
    togglePause();
  }
  parent.addEventListener('click', onBannerClick);

  // Sync dots on manual drag/scroll
  track.addEventListener('scroll', () => {
    const idx = Math.round(track.scrollLeft / track.offsetWidth);
    if (idx !== cur) { cur = idx; sync(cur); }
  }, { passive: true });

  // IntersectionObserver: pause when off-screen (only if not manually paused)
  const observer = new IntersectionObserver(([entry]) => {
    if (paused) return;
    entry.isIntersecting ? startTimer() : stopTimer();
  }, { threshold: 0.25 });
  observer.observe(parent);

  // CSS active-slide class for per-slide entrance animation
  function applyActiveClass() {
    const slides = track.children;
    Array.from(slides).forEach((s, i) => {
      s.classList.toggle('is-active', i === cur);
      s.classList.toggle('was-active', i !== cur);
    });
  }
  track.addEventListener('scroll', applyActiveClass, { passive: true });
  applyActiveClass();

  // Inject heartbeat button after DOM is ready
  injectHeartbeatBtn();

  // Initial height sync + re-sync on resize
  syncHeight();
  window.addEventListener('resize', syncHeight, { passive: true });

  // Re-sync after scroll-snap settles (user drag)
  track.addEventListener('scrollend', () => {
    const idx = Math.round(track.scrollLeft / track.offsetWidth);
    if (idx !== cur) { cur = idx; sync(cur); }
  }, { passive: true });

  // Expose controls globally so inline onclick still works
  const prefix = track.id ? track.id.replace('-track', '') : 'carousel';
  window[`${prefix}GoTo`]  = goTo;
  window[`${prefix}Step`]  = step;
  window[`${prefix}Pause`] = pause;
  window[`${prefix}Resume`] = resume;

  return { goTo, step, startTimer, stopTimer, pause, resume, togglePause };
}

export function initHScroll({ section, track, cardW = 240, interval = 3500 } = {}) {
  if (!track) return;

  let timer = null;
  let forward = true;
  let paused = false;
  const root = section || track;

  function advance() {
    if (paused) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft >= maxScroll - 10) { forward = false; }
    if (track.scrollLeft <= 0) { forward = true; }
    track.scrollBy({ left: forward ? cardW : -cardW, behavior: 'smooth' });
  }

  function startTimer() {
    if (timer) return;
    timer = setInterval(advance, interval);
  }

  function stopTimer() {
    clearInterval(timer);
    timer = null;
  }

  // Pause on user interaction (touch/mouse), resume after 3s
  function onUserInteract() {
    stopTimer();
    clearTimeout(track._resumeT);
    track._resumeT = setTimeout(() => { if (!paused) startTimer(); }, 3000);
  }
  track.addEventListener('touchstart', onUserInteract, { passive: true });
  track.addEventListener('mousedown',  onUserInteract, { passive: true });

  // ── Heartbeat button for h-scroll sections ──
  function injectHScrollHeartbeat() {
    const header = root.querySelector('.svc-sc__header, .sc-header');
    if (!header || header.querySelector('.hb-pause-btn-sm')) return;
    const btn = document.createElement('button');
    btn.className = 'hb-pause-btn-sm';
    btn.setAttribute('aria-label', 'Pause auto-scroll');
    btn.title = 'Pause / Resume';
    btn.innerHTML = `<span class="hb-ring-sm"></span>⏸`;
    btn.addEventListener('click', () => {
      paused = !paused;
      btn.innerHTML = paused ? `<span class="hb-ring-sm"></span>▶` : `<span class="hb-ring-sm"></span>⏸`;
      btn.setAttribute('aria-label', paused ? 'Resume auto-scroll' : 'Pause auto-scroll');
      paused ? stopTimer() : startTimer();
    });
    header.appendChild(btn);
  }
  injectHScrollHeartbeat();

  // IntersectionObserver: pause when off-screen
  const observer = new IntersectionObserver(([entry]) => {
    if (paused) return;
    entry.isIntersecting ? startTimer() : stopTimer();
  }, { threshold: 0.2 });
  observer.observe(root);

  return { startTimer, stopTimer };
}

/**
 * Animate elements inside a section as it scrolls into view.
 * Adds class 'anim-in' to .anim targets inside `root` when visible.
 */
export function initScrollAnimate(root) {
  if (!root) return;
  const targets = root.querySelectorAll('.anim');
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('anim-in'); obs.unobserve(e.target); }
    });
  }, { threshold: 0.15 });
  targets.forEach(t => obs.observe(t));
}
